import React from 'react';
import { GenericPage } from './GenericPage';
import { SignalRibbon } from '../components/Graphics';
export function Platform() {
  return <GenericPage hero={{
    eyebrow: 'Picus Platform',
    headline: 'The AI-native platform for autonomous exposure validation',
    subhead: 'Picus Platform converges Breach and Attack Simulation, Exposure Validation, and Automated Penetration Testing into one real-time, context-rich validation system. Picus Swarm extends that platform with governed agentic operations that reduce the manual gap between finding and action.',
    primaryCta: {
      label: 'Get a Demo',
      to: '/contact'
    },
    secondaryCta: {
      label: 'See Integrations',
      to: '/integrations'
    },
    graphic: <div className="grid grid-cols-1 md:grid-cols-2 gap-8 text-left mt-12" data-id="element-503">
            <div className="border border-picus-border bg-picus-surface p-8" data-id="element-504">
              <h3 className="text-picus-accent font-medium mb-6 uppercase tracking-widest text-sm" data-id="element-505">
                Validation Motions
              </h3>
              <ul className="space-y-4 text-white" data-id="element-506">
                <li className="flex items-center gap-3" data-id="element-507">
                  <div className="w-2 h-2 bg-picus-accent rounded-full" data-id="element-508" />{' '}
                  Breach and Attack Simulation
                </li>
                <li className="flex items-center gap-3" data-id="element-509">
                  <div className="w-2 h-2 bg-picus-accent rounded-full" data-id="element-510" />{' '}
                  Exposure Validation
                </li>
                <li className="flex items-center gap-3" data-id="element-511">
                  <div className="w-2 h-2 bg-picus-accent rounded-full" data-id="element-512" />{' '}
                  Automated Penetration Testing
                </li>
                <li className="flex items-center gap-3" data-id="element-513">
                  <div className="w-2 h-2 bg-picus-accent rounded-full" data-id="element-514" /> Picus
                  Swarm Workflow
                </li>
              </ul>
            </div>
            <div className="border border-picus-border bg-picus-surface p-8" data-id="element-515">
              <h3 className="text-picus-accent font-medium mb-6 uppercase tracking-widest text-sm" data-id="element-516">
                Validation Domains
              </h3>
              <ul className="space-y-4 text-white" data-id="element-517">
                <li className="flex items-center gap-3" data-id="element-518">
                  <div className="w-2 h-2 bg-picus-muted rounded-full" data-id="element-519" />{' '}
                  Security Control Validation
                </li>
                <li className="flex items-center gap-3" data-id="element-520">
                  <div className="w-2 h-2 bg-picus-muted rounded-full" data-id="element-521" /> Attack
                  Surface Validation
                </li>
                <li className="flex items-center gap-3" data-id="element-522">
                  <div className="w-2 h-2 bg-picus-muted rounded-full" data-id="element-523" /> Cloud
                  Security Validation
                </li>
                <li className="flex items-center gap-3" data-id="element-524">
                  <div className="w-2 h-2 bg-picus-muted rounded-full" data-id="element-525" /> Attack
                  Path Validation
                </li>
                <li className="flex items-center gap-3" data-id="element-526">
                  <div className="w-2 h-2 bg-picus-muted rounded-full" data-id="element-527" />{' '}
                  Detection Rule Validation
                </li>
              </ul>
            </div>
          </div>
  }} modules={[{
    title: 'Point tools create fragmented answers',
    subtitle: 'Security teams do not need another isolated signal. They need one platform that connects controls, exposures, exploitability, and action.',
    type: 'cards',
    cards: [{
      title: 'Disconnected Tools',
      description: 'Separate dashboards for scanners, BAS, and pen testing.'
    }, {
      title: 'Picus Platform',
      description: 'One unified system feeding a validated action stream.'
    }],
    graphic: 'Three disconnected tools on left feeding separate dashboards; one Picus Platform on right feeding a validated action stream.'
  }, {
    title: 'Autonomous exposure validation starts with context',
    subtitle: 'Anyone can run a fast script or automate a test. Picus Platform runs validation in the context of your real environment — your controls, your assets, your paths, your policies, your detections, and your workflow.',
    type: 'text',
    graphic: 'Context wheel around a validation core: controls, assets, identity, paths, policy, detections, business criticality.'
  }, {
    title: 'Validation works better when it works together',
    subtitle: 'BAS proves whether controls work. Exposure Validation proves what matters. Automated Penetration Testing proves exploitability and attack paths. Picus Platform brings those methods together.',
    type: 'custom',
    content: <SignalRibbon data-id="element-528" />
  }, {
    title: 'Picus Platform spans the full validation surface',
    subtitle: 'Picus Platform combines core validation motions with broad validation domains across the stack. That gives teams breadth, depth, and a common system for turning evidence into action.',
    type: 'text',
    graphic: '2-column matrix. Left: BAS, Exposure Validation, Automated Penetration Testing, Swarm. Right: Security Control Validation, Attack Surface Validation, Cloud Security Validation, Attack Path Validation, Detection Rule Validation.'
  }, {
    title: 'Security Control Validation',
    subtitle: 'Validate the controls you already paid for. Security Control Validation helps teams prove whether prevention and detection controls are working as expected. It turns assumptions about the stack into evidence about the stack.',
    type: 'text',
    graphic: 'Control layer scorecard with pass/fail indicators across prevention and detection.'
  }, {
    title: 'Attack Surface Validation',
    subtitle: 'Validate what is exposed and why it matters. Attack Surface Validation helps teams move beyond inventory and discovery by validating which exposed assets and weaknesses actually create meaningful risk.',
    type: 'text',
    graphic: 'External asset map with validated exposure highlights.'
  }, {
    title: 'Cloud Security Validation',
    subtitle: 'Validate cloud changes before they become cloud risk. Cloud Security Validation helps teams test how cloud posture, controls, and attack paths hold up as environments change.',
    type: 'text',
    graphic: 'Cloud environment with validated posture checks and drift indicators.'
  }, {
    title: 'Attack Path Validation',
    subtitle: 'See the paths that matter most. Attack Path Validation helps teams understand how weaknesses connect into meaningful routes to impact. It turns isolated findings into exploitability stories.',
    type: 'text',
    graphic: 'Multi-step attack path with one validated critical route highlighted.'
  }, {
    title: 'Detection Rule Validation',
    subtitle: 'Validate the rules your SOC relies on. Detection Rule Validation helps teams prove whether rules actually fire when they should and whether detections are tuned to real attack behavior.',
    type: 'text',
    graphic: 'Detection rule coverage matrix with tested/untested/failing indicators.'
  }, {
    title: 'Turn signals into validated action',
    subtitle: 'Picus ingests signals from across the stack, validates what changed, and helps route the right next step. The result is less manual drag between finding and fix.',
    type: 'cards',
    cards: [{
      title: 'Signal',
      description: 'Ingest data from your environment.'
    }, {
      title: 'Validate',
      description: 'Test controls and exposures.'
    }, {
      title: 'Prioritize',
      description: 'Score based on real context.'
    }, {
      title: 'Route',
      description: 'Send to the right owner.'
    }],
    graphic: 'Signal → validate → prioritize → route → revalidate flow.'
  }, {
    title: 'Built to keep up with faster change',
    subtitle: 'The issue is not that humans are too slow. The issue is that manual handoffs, manual triage, and manual coordination do not scale as threats and environments change faster.',
    type: 'custom',
    content: <div className="mt-8" data-id="element-529">
              <a href="/platform/picus-swarm" className="inline-flex items-center px-6 py-3 border border-picus-border text-white rounded-full hover:bg-picus-surface transition-colors" data-id="element-530">
                Explore Picus Swarm
              </a>
            </div>,
    graphic: 'Manual-only workflow on left versus governed, signal-driven operating model on right.'
  }]} data-id="element-502" />;
}